import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { describePhoneConflict, type PhoneConflict } from "@/lib/start-lead-conversation";

type PhoneRow = { id: string; phone: string; is_primary: boolean };

async function findConflict(workspaceId: string, phone: string, leadId: string | null) {
  const { data } = await supabase.rpc("find_lead_phone_conflict", {
    p_workspace_id: workspaceId,
    p_phone: phone,
    p_exclude_lead_id: leadId,
  });
  return (data as PhoneConflict | null) ?? null;
}

export async function insertDraftLeadPhones(
  workspaceId: string,
  leadId: string,
  phones: string[],
) {
  const unique = [...new Set(phones.map((p) => p.replace(/\D/g, "")).filter((p) => p.length >= 8))];
  if (unique.length === 0) return;
  await supabase.from("lead_phones").insert(
    unique.map((phone) => ({
      lead_id: leadId,
      workspace_id: workspaceId,
      phone,
      is_primary: false,
    })),
  );
}

export function LeadExtraPhonesEditor({
  workspaceId,
  leadId,
  draftPhones,
  onDraftPhonesChange,
}: {
  workspaceId: string;
  leadId: string | null;
  draftPhones?: string[];
  onDraftPhonesChange?: (phones: string[]) => void;
}) {
  const { toast } = useToast();
  const [rows, setRows] = useState<PhoneRow[]>([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    void supabase
      .from("lead_phones")
      .select("id, phone, is_primary")
      .eq("lead_id", leadId)
      .eq("workspace_id", workspaceId)
      .order("is_primary", { ascending: false })
      .then(({ data }) => {
        if (!cancelled) setRows((data as PhoneRow[]) ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, workspaceId]);

  const addPhone = async () => {
    const digits = draft.replace(/\D/g, "");
    if (digits.length < 8) return;
    const conflict = await findConflict(workspaceId, digits, leadId);
    if (conflict) {
      toast({ title: "Número já cadastrado", description: describePhoneConflict(conflict), variant: "destructive" });
      return;
    }
    if (!leadId) {
      onDraftPhonesChange?.([...(draftPhones ?? []), digits]);
      setDraft("");
      return;
    }
    const { error } = await supabase.from("lead_phones").insert({
      lead_id: leadId,
      workspace_id: workspaceId,
      phone: digits,
      is_primary: false,
    });
    if (error) {
      toast({ title: "Não foi possível adicionar", description: error.message, variant: "destructive" });
      return;
    }
    setDraft("");
    const { data } = await supabase
      .from("lead_phones")
      .select("id, phone, is_primary")
      .eq("lead_id", leadId);
    setRows((data as PhoneRow[]) ?? []);
  };

  const makePrimary = async (row: PhoneRow) => {
    if (!leadId) return;
    await supabase.from("lead_phones").update({ is_primary: false }).eq("lead_id", leadId).eq("is_primary", true);
    await supabase.from("lead_phones").update({ is_primary: true }).eq("id", row.id);
    const { data } = await supabase.from("lead_phones").select("id, phone, is_primary").eq("lead_id", leadId);
    setRows((data as PhoneRow[]) ?? []);
  };

  const removePhone = async (row: PhoneRow) => {
    if (row.is_primary) {
      toast({ title: "O telefone principal não pode ser removido aqui", description: "Troque o principal antes." });
      return;
    }
    await supabase.from("lead_phones").delete().eq("id", row.id);
    setRows((prev) => prev.filter((item) => item.id !== row.id));
  };

  const listed = leadId ? rows.filter((row) => !row.is_primary) : (draftPhones ?? []).map((phone) => ({ id: phone, phone, is_primary: false }));

  return (
    <div className="space-y-2">
      <Label>Outros telefones</Label>
      <p className="text-xs text-muted-foreground">
        Cada número abre uma conversa própria. Até 4 além do principal.
      </p>
      {listed.map((row) => (
        <div key={row.id} className="flex items-center gap-2 text-sm">
          <span className="tabular-nums flex-1">{row.phone}</span>
          {leadId ? (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => void makePrimary(row as PhoneRow)}>
                Tornar principal
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => void removePhone(row as PhoneRow)}>
                Remover
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDraftPhonesChange?.((draftPhones ?? []).filter((phone) => phone !== row.phone))}
            >
              Remover
            </Button>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Outro número"
          inputMode="numeric"
        />
        <Button type="button" variant="outline" onClick={() => void addPhone()}>
          Adicionar
        </Button>
      </div>
    </div>
  );
}
